import { ACCOUNT_PROVIDERS, api } from '@agency/shared';
import { Button, Chip, DataTable, Dialog, Field, Input, MetricCell, MetricRow, PageHeader, Select, StatusDot, Textarea, fmtCount, type DataTableColumn } from '@agency/ui';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorCallout, FilterInput, FilterSelect, IdCell, LoadMore, Page, Stamp, TableCard } from '../../app/widgets.js';
import { HEALTH_TONE, PROVIDER_LABEL, pluralize } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi, usePaged } from '../../lib/useApi.js';

type Row = api.adminAccounts.AccountRegistryEntry;

/** One sentence, reused by the list and the detail page: rows are reported by devices; never a gate. */
export const REGISTRY_NOTE = 'Rows are reported by desktop devices: operators add accounts locally and the registry mirrors what their devices report.';
export const COUNTING_NOTE = 'Unique customers, provider connections and local copies are counted separately.';

export function AccountsListPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<string | undefined>(undefined);
  const [lifecycle, setLifecycle] = useState<string | undefined>('active');
  const [search, setSearch] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const input = useMemo(() => ({ limit: 100, ...(provider ? { provider: provider as api.Account['provider'] } : {}), ...(lifecycle ? { lifecycle: lifecycle as api.AccountLifecycle } : {}), ...(search ? { search } : {}) }), [provider, lifecycle, search]);
  const accounts = usePaged('admin.accounts.list', { input });
  const stats = useApi('admin.accounts.stats');
  const canRegister = session.has('account.register');

  const columns = useMemo<DataTableColumn<Row>[]>(
    () => [
      { id: 'id', header: 'Stable id', cell: ({ row }) => <IdCell value={row.original.id} tone="brand" />, size: 110 },
      { id: 'name', header: 'Name', accessorKey: 'displayName', cell: ({ row }) => <span className="text-ink">{row.original.displayName}</span>, size: 220 },
      { id: 'provider', header: 'Provider', cell: ({ row }) => <Chip tone={row.original.provider === 'template_only' ? 'mute' : 'info'}>{PROVIDER_LABEL[row.original.provider]}</Chip>, size: 90 },
      { id: 'operators', header: 'Operators', cell: ({ row }) => <span className="block text-right font-mono tabular-nums" title="Users whose devices report this account locally">{row.original.operatorCount}</span>, size: 110 },
      { id: 'devices', header: 'Devices', cell: ({ row }) => <span className="block text-right font-mono tabular-nums" title="Devices reporting this account as present">{row.original.deviceCount}</span>, size: 80 },
      {
        id: 'connections',
        header: 'Connections present',
        size: 200,
        cell: ({ row }) => (
          <span className="flex items-center justify-between gap-2">
            <span className="font-mono tabular-nums">{row.original.connectionCount}</span>
            {row.original.worstHealth ? <StatusDot tone={HEALTH_TONE[row.original.worstHealth]} label={row.original.worstHealth} /> : <span className="text-mute">--</span>}
          </span>
        ),
      },
      { id: 'sync', header: 'Last reported sync', cell: ({ row }) => <span className="flex items-center gap-2"><Stamp value={row.original.lastReportedSyncAt} relative />{row.original.lastReportedSyncAt ? <span className="font-mono text-2xs uppercase text-dim">device reported</span> : null}</span>, size: 190 },
      { id: 'lifecycle', header: 'Archived', cell: ({ row }) => <Chip tone={row.original.lifecycle === 'archived' ? 'mute' : 'ok'}>{row.original.lifecycle}</Chip>, size: 90 },
    ],
    [],
  );

  const s = stats.data;
  return (
    <Page>
      <PageHeader
        eyebrow="Accounts and devices"
        eyebrowPath="Accounts"
        title="Account registry"
        subtitle={s ? `${pluralize(s.uniqueCustomers, 'unique customer')} · ${pluralize(s.operators, 'operator')} · ${pluralize(s.devicesReporting, 'device')} reporting · ${s.providerConnections} provider connections · ${s.archivedAccounts} archived` : 'Loading registry.'}
        actions={<Button variant="primary" icon={Plus} disabled={!canRegister} onClick={() => setRegisterOpen(true)}>Register account</Button>}
      />
      <ErrorCallout error={accounts.error ?? stats.error} onRetry={() => void accounts.reload()} className="mt-6" />
      {s ? (
        <div className="mt-6">
          <MetricRow>
            <MetricCell label="Unique customers" value={fmtCount(s.uniqueCustomers)} />
            <MetricCell label="Operators" value={fmtCount(s.operators)} />
            <MetricCell label="Devices reporting" value={fmtCount(s.devicesReporting)} />
            <MetricCell label="Provider connections" value={fmtCount(s.providerConnections)} />
            <MetricCell label="By provider" value={<span className="font-mono text-sm">{ACCOUNT_PROVIDERS.map((p) => `${PROVIDER_LABEL[p]} ${s.byProvider[p] ?? 0}`).join(' · ')}</span>} />
          </MetricRow>
          <p className="mt-2 text-xs text-mute">{COUNTING_NOTE} Operators and devices are counted from presence reports.</p>
        </div>
      ) : null}
      <div className="mt-6">
        <TableCard title="Registry" subtitle={REGISTRY_NOTE} footer={<LoadMore hasMore={accounts.hasMore} loading={accounts.loadingMore} onLoadMore={() => void accounts.loadMore()} />}>
          <div className="border-b border-hair px-5 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <FilterInput label="Search" value={search} onChange={setSearch} placeholder="Name" width={240} />
              <FilterSelect label="Provider" value={provider} onChange={setProvider} options={ACCOUNT_PROVIDERS.map((p) => ({ value: p, label: PROVIDER_LABEL[p] }))} width={140} />
              <FilterSelect label="Archived" value={lifecycle} onChange={setLifecycle} options={[{ value: 'active', label: 'active' }, { value: 'archived', label: 'archived' }]} width={140} />
              <span className="ml-auto font-mono text-2xs uppercase tracking-[0.18em] text-dim">{accounts.items.length} shown</span>
            </div>
          </div>
          <DataTable<Row>
            aria-label="Account registry"
            columns={columns}
            rows={accounts.items}
            rowKey={(r) => r.id}
            height={Math.min(720, Math.max(240, accounts.items.length * 34 + 40))}
            state={accounts.loading ? 'loading' : accounts.items.length === 0 ? 'empty' : 'complete'}
            emptyMessage="No accounts match these filters."
            noun="accounts"
            onSelect={(id) => void navigate({ to: '/accounts/$accountId', params: { accountId: id } })}
            onActivate={(id) => void navigate({ to: '/accounts/$accountId', params: { accountId: id } })}
          />
        </TableCard>
      </div>
      <RegisterAccountDialog open={registerOpen} onOpenChange={setRegisterOpen} />
    </Page>
  );
}

/** Pre-creates a registry entry with a server-issued stable id so `From registry` on the desktop can find it. */
function RegisterAccountDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const mutation = useMutation();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [provider, setProvider] = useState<api.Account['provider']>('vanta');
  const [notes, setNotes] = useState('');
  const valid = displayName.trim().length > 0;
  const submit = async () => {
    const r = await mutation.run('admin.accounts.create', { input: { displayName: displayName.trim(), provider, ...(notes ? { notes } : {}), assignedUserIds: [] } }, { title: (d) => `Registered ${d.account.displayName}`, invalidate: ['admin.accounts', 'admin.overview'] });
    if (r) {
      onOpenChange(false);
      setDisplayName('');
      setNotes('');
      void navigate({ to: '/accounts/$accountId', params: { accountId: r.account.id } });
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Register account" subtitle="Pre-creates a registry entry with a server-issued stable id" width={520} actions={<><Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!valid} loading={mutation.busy} onClick={() => void submit()}>Register</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Name" required><Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Customer name as shown to operators" autoFocus /></Field>
        <Field label="Provider" required><Select value={provider} onValueChange={(v) => setProvider(v as api.Account['provider'])} options={ACCOUNT_PROVIDERS.map((p) => ({ value: p, label: PROVIDER_LABEL[p] }))} /></Field>
        <Field label="Notes" hint="OPTIONAL" help="Internal note. Never customer findings."><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <p className="text-xs text-mute">Operators pick this entry from the registry on their desktop; nothing is assigned or enforced here.</p>
      </div>
    </Dialog>
  );
}
